const moneyFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2,
  minimumFractionDigits: 0,
});

/**
 * Format a numeric amount with thousands separators (no currency symbol).
 * @param {unknown} value
 * @returns {string | null}
 */
export function formatMoneyAmount(value) {
  if (value == null || value === '') return null;
  const num =
    typeof value === 'number'
      ? value
      : parseFloat(String(value).replace(/,/g, ''), 10);
  if (!Number.isFinite(num)) return null;
  return moneyFormatter.format(num);
}

/**
 * Format a numeric amount as USD (e.g. "$1,250" or "$89.50").
 * @param {unknown} value
 * @returns {string | null}
 */
export function formatCurrency(value) {
  const amount = formatMoneyAmount(value);
  if (amount == null) return null;
  return `$${amount}`;
}

/**
 * @param {unknown} min
 * @param {unknown} max
 * @param {{ separator?: string }} [options]
 * @returns {string | null}
 */
export function formatCurrencyRange(min, max, options = {}) {
  const separator = options.separator ?? ' – ';
  const minLabel = formatCurrency(min);
  const maxLabel = formatCurrency(max);
  if (minLabel && maxLabel) return `${minLabel}${separator}${maxLabel}`;
  if (minLabel) return `From ${minLabel}`;
  if (maxLabel) return `Up to ${maxLabel}`;
  return null;
}

/**
 * Normalize PriceType strings from API / legacy payloads.
 * @param {unknown} priceType
 */
function normalizePriceType(priceType) {
  if (priceType == null || priceType === '') return null;
  const pt = String(priceType).trim();
  const lower = pt.toLowerCase();
  if (lower === 'n/a' || lower === 'na') return 'NA';
  if (lower === 'fixed' || pt === 'FixedPrice') return 'Fixed';
  if (lower === 'range') return 'Range';
  if (lower === 'dynamic' || pt === 'DynamicPrice') return 'Dynamic';
  return pt;
}

function readPricingBounds(pricing) {
  const nested = pricing.PriceRange ?? pricing.priceRange ?? {};
  const min =
    pricing.Min ??
    pricing.min ??
    nested.Min ??
    nested.min;
  const max =
    pricing.Max ??
    pricing.max ??
    nested.Max ??
    nested.max;
  return { min, max };
}

/**
 * Merge API and legacy pricing shapes into one object for display.
 * @param {object | null | undefined} pricing
 * @returns {object | null}
 */
export function normalizePricingForDisplay(pricing) {
  if (!pricing || typeof pricing !== 'object') return null;

  const nested = pricing.PriceRange ?? pricing.priceRange ?? {};
  let priceType = normalizePriceType(pricing.PriceType ?? pricing.priceType);
  const { min, max } = readPricingBounds(pricing);

  const fixedValue =
    pricing.Price ??
    pricing.price ??
    pricing.FixedPrice ??
    pricing.fixedPrice ??
    nested.FixedPrice ??
    nested.fixedPrice;

  if (!priceType) {
    if (min != null && min !== '' && max != null && max !== '') {
      priceType = 'Range';
    } else if (min != null && min !== '') {
      priceType = 'Range';
    } else if (max != null && max !== '') {
      priceType = 'Range';
    } else if (fixedValue != null && fixedValue !== '') {
      priceType = 'Fixed';
    }
  }

  if (!priceType) return null;

  const normalized = { PriceType: priceType };

  if (priceType === 'Fixed' && fixedValue != null && fixedValue !== '') {
    normalized.Price = fixedValue;
  }

  if (priceType === 'Range') {
    if (min != null && min !== '') normalized.Min = min;
    if (max != null && max !== '') normalized.Max = max;
  }

  return normalized;
}

/**
 * Format a Pricing object for display.
 * Supports API shape (Price, Min, Max) and legacy (FixedPrice, PriceRange).
 *
 * @param {object | null | undefined} pricing
 * @param {{ fixedPrefix?: 'from' | 'none', rangeSeparator?: string }} [options]
 * @returns {string | null}
 */
export function formatPricingLabel(pricing, options = {}) {
  const normalized = normalizePricingForDisplay(pricing);
  if (!normalized) return null;

  const fixedPrefix = options.fixedPrefix ?? 'from';
  const rangeSeparator = options.rangeSeparator ?? ' – ';
  const pt = normalized.PriceType;

  if (pt === 'Fixed') {
    const label = formatCurrency(normalized.Price);
    if (!label) return null;
    return fixedPrefix === 'from' ? `From ${label}` : label;
  }

  if (pt === 'Range') {
    return formatCurrencyRange(normalized.Min, normalized.Max, {
      separator: rangeSeparator,
    });
  }

  if (pt === 'NA') return 'Contact for pricing';

  if (pt === 'Dynamic') return 'Custom quote';

  return null;
}

/**
 * Wizard / provider search cards — non-null fallback when pricing is missing.
 * @param {object | null | undefined} pricing
 * @returns {string}
 */
export function formatSubservicePricingLabel(pricing) {
  return formatPricingLabel(pricing) ?? 'Contact for pricing';
}
